import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../common/hooks/hooks";
import { useGetCheckoutDataQuery } from "../../api/checkoutApi";
import { setSelectedPaymentMethod } from "../../checkoutSlice";
import { selectSelectedPaymentMethod } from "../../checkoutSelectors";
import { useUpdatePaymentMethod } from "../../hooks/useUpdatePaymentMethod";
import Icon from "../../../../../common/components/Icon/Icon";

const PaymentOptionsSkeleton: React.FC = () => (
  <div className="payment-options__list">
    {Array.from({ length: 3 }).map((_, i) => (
      <div key={i} className="payment-option-skeleton">
        <Skeleton height={50} />
      </div>
    ))}
  </div>
);

const PaymentOptions: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedMethodId = useAppSelector(selectSelectedPaymentMethod);

  // 1. Fetch ALL checkout data, which now includes payment methods
  const { data: checkoutData, isLoading, isError } = useGetCheckoutDataQuery();
  const paymentMethods = checkoutData?.availablePaymentMethods;

  // 2. Consume the custom hook for server synchronization
  const { handleUpdatePaymentMethod, isUpdating } = useUpdatePaymentMethod();

  // 3. Access localized gateway constants
  const gatewayIds = window.commonVariables?.paymentGatewayIds;

  // 4. Initial Sync: Ensure Redux matches the server's Draft Order state
  useEffect(() => {
    if (checkoutData?.paymentMethod && !selectedMethodId) {
      dispatch(setSelectedPaymentMethod(checkoutData.paymentMethod));
    } else if (
      paymentMethods &&
      paymentMethods.length > 0 &&
      !selectedMethodId &&
      !isLoading
    ) {
      // Fallback: If server has no method set yet, auto-select the first one
      const firstMethod = paymentMethods[0].id;
      handleUpdatePaymentMethod(firstMethod).then((result) => {
        if (result.success) {
          dispatch(setSelectedPaymentMethod(firstMethod));
        }
      });
    }
  }, [
    checkoutData,
    paymentMethods,
    selectedMethodId,
    isLoading,
    dispatch,
    handleUpdatePaymentMethod,
  ]);

  /**
   * Handles the change event for the payment method radio buttons.
   * Logic: Wait for the server to confirm the change before updating the UI.
   */
  const handleMethodChange = async (methodId: string) => {
    if (methodId === selectedMethodId || isUpdating) return;

    // Call the server first
    const result = await handleUpdatePaymentMethod(methodId);

    // Only update Redux if the server successfully updated the draft order
    if (result.success) {
      dispatch(setSelectedPaymentMethod(methodId));
    }
  };

  if (isLoading) {
    return <PaymentOptionsSkeleton />;
  }

  if (isError || !paymentMethods || paymentMethods.length === 0) {
    return (
      <div className="alert alert--warning">
        {t("paymentOptions.noPaymentMethods", "No payment methods available.")}
      </div>
    );
  }

  // Helper to determine if a method is a credit card provider
  const isCreditCardMethod = (id: string) => {
    if (!gatewayIds) return false;
    return id === gatewayIds.IYZICO || id === gatewayIds.PAYTR;
  };

  return (
    <div className={`payment-options ${isUpdating ? "is-syncing" : ""}`}>
      <div className="payment-options__list">
        {paymentMethods.map((method) => (
          <label
            key={method.id}
            className={`payment-option ${
              selectedMethodId === method.id ? "is-selected" : ""
            } ${isUpdating ? "is-disabled" : ""}`}
          >
            <div className="payment-option__header">
              <div className="payment-option__radio">
                <input
                  type="radio"
                  name="payment_method"
                  value={method.id}
                  checked={selectedMethodId === method.id}
                  onChange={() => handleMethodChange(method.id)}
                  disabled={isUpdating}
                />
              </div>
              <span className="payment-option__label">{method.title}</span>

              {/* Icon Logic using Constants */}
              <div className="payment-option__icon">
                {method.icon ? (
                  <span dangerouslySetInnerHTML={{ __html: method.icon }} />
                ) : (
                  <>
                    {gatewayIds && method.id === gatewayIds.BACS && (
                      <Icon iconName="building-library" />
                    )}
                    {/* Generic Credit Card Icon for both Iyzico and PayTR */}
                    {isCreditCardMethod(method.id) && (
                      <Icon
                        iconName="credit-card-security"
                        className="icon--filled"
                      />
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Description Area - Only visible if selected */}
            {selectedMethodId === method.id && method.description && (
              <div className="payment-option__content">
                <p dangerouslySetInnerHTML={{ __html: method.description }}></p>
              </div>
            )}
          </label>
        ))}
      </div>
    </div>
  );
};

export default PaymentOptions;
